import React from 'react';
import { ViewModel, getSafeRandom, getMultiLang } from '@platform/api';
import { isValidType } from '@platform/template';
import SimpleTable from './Table';
import FlatButton from './components/FlatButton';
import { createCardTable, createRelation } from '../CardTable/hooks';
import GridTable from "../GridTable/Table";

const generateKey = () => String(new Date().getTime()).slice(-5) + getSafeRandom().toString(12);
/**
 * 创建SimpleTable表格
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {Config} config simpleTable的配置项
 * @returns {SimpleTableComponent}
 */
export function createSimpleTable(moduleId, config = {}) {
    if (!moduleId) { return null; }
    let store = getStore.call(this, moduleId) || setStore.call(this, moduleId);
    let meta = this.state.meta;
    let loadedMeta;
    if (meta) {
        loadedMeta = Object.keys(meta).length;
    }

    /** @detail 必须先调用use方法并且设置模板，否则不渲染 */
    if (!store) {
        console.warn(`请先调用this.props.use.table('${moduleId}');`);
        return null;
    }

    if (!loadedMeta) { return null; }
    let _runContext = this.getQiankunContext();
    let layoutData = this.getCompPropFromLayout(moduleId) || {};
    let { layoutMode, childTableCode, childPkName } = _getFlatConfig.call(this, layoutData, config, moduleId, meta);
    let flatConfig = { ...this.getCompPropFromLayout(childTableCode[0]), ...flatConfig } || {}; //拉平表的config
    if (Object.keys(layoutData).length) {
        config.showIndex = !layoutData.hiddenIndex;
    }
    config = {
        id: moduleId,
        ...layoutData,
        layoutMode,
        childPkName,
        flatConfig,
        ...config,
        childTableCode, //低代码会在config里传这个，这里覆盖下  
    };
    let { childConfig = {} } = config;
    let { multiConfig = {} } = childConfig;
    store?.setConfig(config);

    // window['page_test'] = this;
    //布局模式
    if (layoutMode === "vertical") { //上下表
        config.adaptionHeight = true;
        childTableCode.forEach((childModuleId, index) => {
            let endTable = index === childTableCode.length - 1;
            this.tableRelation = this.tableRelation || {};
            this.tableRelation[childModuleId] = this.tableRelation[childModuleId] ?? createRelation.call(this, this.state.meta, childModuleId);
            let tabs = this.tableRelation[childModuleId]?.tabs;
            tabs[childModuleId]?.forEach((tabId, index) => {
                if (index === 0) {
                    childConfig = { adaptionHeight: endTable, ...childConfig, layoutMode: "vertical" };
                } else {
                    childConfig.multiConfig = { [tabId]: { adaptionHeight: endTable, ...multiConfig[tabId] } };
                }
            });
        });
        return <>
            <SimpleTable
                {...config}
                key={moduleId}
                id={moduleId}
                store={store}
                pageScope={this}
                _runContext={_runContext}
            />
            {childTableCode.map(id => {
                return createCardTable.call(this, id, childConfig);
            })}
        </>;
    } else if (layoutMode === "horizontal") {
        return;
    } else { //默认模式和主子拉平模式
        return <SimpleTable
            {...config}
            key={moduleId}
            id={moduleId}
            store={store}
            pageScope={this}
            _runContext={_runContext}
        />;
    }

}

function _getFlatConfig(layoutData, config, moduleId, meta) {
    let { childTableCode = [] } = layoutData;
    let layoutMode;
    let childPkName;
    childTableCode = typeof childTableCode === "string" ? childTableCode.split(',') : childTableCode;
    if (childTableCode.length && childTableCode[0] !== '') {
        layoutMode = config.layoutMode || "flat";
        setFlatIdRelation.call(this, { moduleId, childTableCode });
        let columns = meta[childTableCode].items;
        if (layoutMode === "flat") {
            columns.forEach(column => {
                let { metapath } = column;
                let bodypks = Object.values(meta.metaJson?.head?.bodypks || {});
                bodypks.forEach(bodypk => {
                    if (metapath && metapath.indexOf(bodypk.foreignKey) !== -1 && !childPkName) {
                        childPkName = bodypk.primaryKey; //设置子表pk
                    }
                });
            });
        }
    }
    return { childTableCode, layoutMode, childPkName };
}

/**
 * 设置过滤条件
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @param {{ logic: 'or' | 'and', conditions: Array<{ key: ColKey, value, string, logic: 'equal' | 'greaterThan' | 'lessThan' | 'notIncluded' | 'included' }> }} record
 */
export function setFilterInfo(id, info) {
    let store = getStore.call(this, id);
    store?.setFilterInfo(info);
}

/**
 * 获取过滤条件
 * @memberof SimpleTable
 * @param {ModuleId} id
 */
export function getFilterInfo(id) {
    let store = getStore.call(this, id);
    return store?.getFilterInfo();
}

/**
 * 设置排序条件
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @param {{ logic: 'or' | 'and', conditions: Array<{ key: ColKey, value, string, logic: 'equal' | 'greaterThan' | 'lessThan' | 'notIncluded' | 'included' }> }} record
 */
export function setSortInfo(id, info, { shouldUpdateRows = true } = {}) {
    let store = getStore.call(this, id);
    store?.setSortInfo(info, { shouldUpdateRows });
}

/**
 * 获取排序条件
 * @memberof SimpleTable
 * @param {ModuleId} id
 */
export function getSortInfo(id) {
    let store = getStore.call(this, id);
    return store?.getSortInfo();
}

/**
 * 设置搜索关键字
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @param {string} keyWord
 */
export function setKeyWord(id, keyWord) {
    execWithShare.call(this, id, tabId => {
        getStore.call(this, tabId)?.setKeyWord(keyWord);
    });
}

/**
 * 创建小卡片表格
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @param {CardConfig} config TODO:
 * @returns {GridTableComponent}
 */
export function createSmallCard(id, config = {}) {
    let store = getStore.call(this, id) || setStore.call(this, id),
        tableStore = getStore.call(this, config.tableId);

    config = { ...config, theme: 'simple', gridType: 'small-card' };
    store?.setConfig(config);
    tableStore?.setGridTableId(id);

    if (!store || !this.state.meta) {
        return null;
    }

    return <GridTable
        {...config}
        config={config}
        id={id}
        key={id}
        store={store}
        pageScope={this}
        type='small'
        dataSource={this.tableDataSource}
    />;
}

/**
 * 创建大卡片表格
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @param {CardConfig} config TODO:
 * @returns {GridTableComponent}
 */
export function createLargeCard(id, config = {}) {
    let store = getStore.call(this, id) || setStore.call(this, id),
        tableStore = getStore.call(this, config.tableId);

    config = { ...config, theme: 'simple', gridType: 'large-card' };
    store?.setConfig(config);
    tableStore?.setGridTableId(id);

    if (!store || !this.state.meta) {
        return null;
    }

    return <GridTable
        {...config}
        config={config}
        id={id}
        key={id}
        store={store}
        pageScope={this}
        type='large'
        dataSource={this.tableDataSource}
    />;
}

/**
 * 聚焦单元格
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 */
export function focusOnCell(id, rowKey, colKey) {
    let store = getStore.call(this, id);
    store.focusOnCell(rowKey, colKey);
}

/**
 * 设置表格数据
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {TableData} data -表格数据
 * @param {boolean} isTop - 2207未实现
 * @param {boolean} shouldForceUpdate - 2207未实现
 * @param {boolean} autoFocus - 是否自动聚焦到第一个元素
 * @param {Object} extraConfig -扩展配置项
 */
export function setAllTableData(moduleId, data, isTop = true, shouldForceUpdate = true, autoFocus = true, extraConfig = {}) {
    moduleId = getParentId.call(this, moduleId); //领域可能传子表ID，这里统一转化成主表ID
    if (!getStore.call(this, moduleId) || !data) { return; }

    execWithShare.call(this, moduleId, shareId => {
        let store = getStore.call(this, shareId),
            pageInfo = store?.getPaginationInfo(),
            { allpks, alltss, allbodypks, pkrelation } = store?.getAllRowsInfo(),
            { crossPageSelect, pkname } = store?.getConfig() || {},
            { tableType } = store.getFlatInfo();

        if (!store) { return; }

        !this.isUpdatePage && store.getInstance().preventUpdate(true);

        try {
            /** @detail 清除排序、筛选、行点击、行选中、行禁用、最后一次check状态、组合排序 */
            store.setClickedRow();
            store.setSelectedCell();
            store.setCopiedCell(); //清空复制单元格
            store.setLastCheckedKey();

            /** @detail 分页数据处理: 携带数据时更新；不携带且rows为空重置；不携带且rows不为空且之前没有设置过allpks时重置；其他情况不改变 */
            if (data.hasOwnProperty('pageInfo')) {
                pageInfo = { ...data.pageInfo, pageIndex: Number(data.pageInfo.pageIndex) };
            } else if (data?.rows?.length === 0) {
                pageInfo = { ...pageInfo, pageIndex: 0, total: 0, totalPage: 1 };
            } else if (!allpks?.length) {
                pageInfo = { ...pageInfo, pageIndex: 0 };
            }
            store.setPaginationInfo(pageInfo);

            /** @detail allpks数据处理：携带数据时更新；不携带且rows为空重置；其他情况不改变 */
            if (data.allpks) {
                allpks = data.allpks;
            } else if (data?.rows?.length === 0) {
                allpks = [];
                allbodypks = [];
            }
            /** @detail alltss数据处理：携带数据时更新；其他情况不改变 */
            if (data.alltss) {
                alltss = data.alltss;
            }
            /** @detail 子表pk */
            if (data.allbodypks) {
                allbodypks = data.allbodypks;
            }
            /** @detail 主子表pkRelation */
            if (data.pkrelation) {
                pkrelation = data.pkrelation;
            }

            /** @detail 情况排序状态 */
            if (extraConfig?.disabledClearSort) {
                store.setSortInfo(store.getSortInfo());
            } else {
                store.setSortInfo();
            }

            /** @detail 清空筛选状态 */
            if (extraConfig?.disabledClearFilter) {
                store.setFilterInfo(store.getFilterInfo());
            } else {
                store.setFilterInfo();
            }

            /** @detail 设置表格数据 */
            store.setRows(data);
            /** @detail 设置pk */
            store.setAllRowsInfo({ allpks, alltss, allbodypks, pkrelation });

            /** @detail 选中数据处理：跨页选（配置了crossPageSelect和pkname)，恢复全选状态/恢复选中状态(pkname值一致) */
            if (crossPageSelect && pkname) { // 当(crossPageSelect && pkname) === true, useCheckbox会渲染下拉菜单。
                let allCheckedKeys = store.getAllCheckedKeys(),
                    allBodyCheckedKeys = store.getAllBodyCheckedKeys(),
                    { childPkName } = store.getConfig(),
                    checkedKeys = new Set(),
                    allpksSet = new Set(allpks);

                if (store.isAllChecked()) {
                    store.setCheckedKeys(new Set(data.rows.map(row => row.key || row.rowid || row.rowId)), { isCheckedAll: true });
                    store.setAllCheckedKeys(new Set(data.allpks || allpks), { isCheckedAll: true });
                    //主子表模式下才有效
                    store.setAllBodyCheckedKeys(new Set(data.allbodypks || []), { isCheckedAll: true });
                } else {
                    if (tableType === "flat") {
                        data.rows.forEach(row => {
                            if (row.values[childPkName]) {
                                let childPk = row.values[childPkName].value;
                                if (allBodyCheckedKeys.has(childPk)) {
                                    checkedKeys.add(row.key || row.rowid || row.rowId);
                                }
                            }
                            if (row.selected) {
                                checkedKeys.add(row.rowId);
                            }
                        });
                        store.setAllBodyCheckedKeys(allBodyCheckedKeys);
                    } else {
                        allCheckedKeys = new Set([...allCheckedKeys].filter(key => allpksSet.has(key)));
                        data.rows.forEach(row => {
                            if (row.values[pkname]) {
                                let pk = row.values[pkname].value;
                                if (allCheckedKeys.has(pk)) {
                                    checkedKeys.add(row.key || row.rowid || row.rowId);
                                }
                            }
                            if (row.selected) {
                                checkedKeys.add(row.rowId);
                            }
                        });
                        store.setAllCheckedKeys(allCheckedKeys);
                    }
                    store.setCheckedKeys(checkedKeys);
                }
            } else {
                /** @detail 非跨页选时清空选中行 */
                let checkedKeys = new Set();
                data.rows.forEach(row => {
                    if (row.selected) {
                        checkedKeys.add(row.rowId);
                    }
                });
                store.setCheckedKeys(checkedKeys);
            }
        } catch (e) {
            console.error(e);
        }

        // YBP-SCM-BUG-2321
        // 主子拉平特殊处理。设置完表格数据之后，统一更新
        window._tableTypeChangeCallback?.();
        if (window._simpleTableGlobalParams && window._simpleTableGlobalParams[moduleId]) {
            window._simpleTableGlobalParams[moduleId].setDataFlag = true;
        }
        !this.isUpdatePage && store.getInstance().preventUpdate(false);
        store.forceUpdate();
    });

    let store = getStore.call(this, moduleId);
    // 自动聚焦
    if (autoFocus && data.rows.length) {
        (function autoFocus() {
            requestAnimationFrame(() => {
                // 聚焦第一个元素
                let leafColumnKeys = store.getVisibleLeafColumnKeys().filter(
                    key => isValidType(store.getColumnProps(key, 'itemtype')),
                );

                if (leafColumnKeys.length) {
                    store.focusOnCell(store.getRows()[0]?.key, leafColumnKeys[0]);
                } else {
                    // DOM还没出来（还没有meta的时候），继续轮询
                    autoFocus();
                }
            });
        })();
    }

    // 已知的与其他表格不同：不需要回调selectedChange
}

/**
 * 供应链加： 设置表格数据(不更改分页和pk信息)
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {TableData} data -表格数据
 */
export function setOnlyTableData(moduleId, data, autoFocus = true, config) {
    let store = getStore.call(this, moduleId);
    if (!store || !data) { return; }
    let pageInfo = store?.getPaginationInfo(),
        { allpks, alltss, allbodypks, pkrelation } = store?.getAllRowsInfo();
    data = {
        ...data,
        pageInfo: pageInfo,
        pkrelation: pkrelation,
        allbodypks: allbodypks,
        allpks: allpks,
        alltss: alltss,
    };
    setAllTableData.call(this, moduleId, data, false, false, autoFocus, config);
}
/**
 * 获取分页信息
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @returns {{ pageIndex: number, pageSize: number }} - 页码：页码从0开始， 每页数量
 */
export function getTablePageInfo(moduleId) {
    let store = getStore.call(this, moduleId),
        info = store.getPaginationInfo();
    return {
        pageIndex: Number(info.pageIndex),
        pageSize: Number(info.pageSize),
    };
}

/**
 * 显示弹窗
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {"add" | "edit"} status TODO:是否有browse状态
 * @param {TableRow} record
 * @param {DataIndex} dataIndex
 */
export function openModel(moduleId, status, record, dataIndex) {
    let store = getStore.call(this, moduleId);

    store?.openSimpleTableModal(status, record, store.getRows()[dataIndex]?.viewIndex);
}

/**
 * 关闭弹窗
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 */
export function closeModel(moduleId) {
    let store = getStore.call(this, moduleId);

    store?.closeSimpleTableModal();
}

/**
 * 设置列的自定义渲染方法
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey
 * @param {function} render
 */
export function setTableRender(moduleId, colKey, render) {
    let store = getStore.call(this, moduleId);
    store.setColumnProps(colKey, { render });
}

/**
 * 设置列精度
 * @memberof SimpleTable
 * @param {Array<{ areacode: ModuleId, fieldcode: ColKey, scale: number }>} params
 */
export function setColScale(params) {
    params.forEach(param => {
        let { areacode: id, fieldcode: colKey, scale } = param,
            store = getStore.call(this, id);

        store.setColumnProps(colKey, { scale });
    });
}

/**
 * 获取列的显示属性
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey
 * @returns {boolean}
 */
export function checkVisible(moduleId, colKey) {
    let store = getStore.call(this, moduleId);
    return store.getColumnProps(colKey, 'visible');
}

/**
 * 获取拖拽后的列宽
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @returns {Object}
 */
export function getAllColumnsWidth(id) {
    let store = getStore.call(this, id);
    return store.getColumnsWidth();
}

/**
 * 获取表格数据
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {boolean} deepClone - 是否进行深拷贝
 * @returns {{ model: boolean, operType: "add" | "edit" | "browse", checkedAll: boolean, allpks: string[], alltss: string[], rows: TableRow[], pageInfo: { pageIndex, pageSize } }} - model：弹窗状态，operType：弹窗类型，checkedAll: 全选状态
 */
export function getAllTableData(moduleId, deepClone) {
    let store = getStore.call(this, moduleId),
        simpleTableModalInfo = store.getSimpleTableModalInfo() || {},
        checkedKeys = store.getCheckedKeys();

    let tableData = {
        rows: store.getRows().map(row => {
            row.selected = checkedKeys.has(row.key || row.rowId);
            return row;
        }),
        pageInfo: store.getPaginationInfo(),
        visibleRows: store.getVisibleRows(),
        ...store.getAllRowsInfo(),
        model: !!simpleTableModalInfo.open,
        operType: simpleTableModalInfo.status || 'browse',
        checkedAll: store.isAllChecked(),
    };

    return deepClone ? JSON.parse(JSON.stringify(tableData)) : tableData;
}

/**
 * 更新包含指定数据的行数据
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {Object<ColKey, CellValue>} record - 指定的列值
 * @param {TableRow} values - 需要合并的数据
 */
export function setTableValueByKeyAndRecord(moduleId, record, values) {
    let store = getStore.call(this, moduleId),
        colValue = record.attrcode.value,
        rows = store.getRows();

    /** @detail 合并数据 */
    record = { ...record, ...values };
    rows = rows.map(row => {
        if (row.values.attrcode && colValue == row.values.attrcode.value) {
            row.values = { ...record };
        }
        return row;
    });

    execWithShare.call(this, moduleId, shareId => {
        let shareStore = getStore.call(this, shareId);
        shareStore?.setRows({ rows });
    });

}

/**
 * 隐藏列
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {ColKey | ColKey[]} colKeys
 */
export function hideColByKey(moduleId, colKeys) {
    !Array.isArray(colKeys) && (colKeys = [colKeys]);
    let store = getStore.call(this, moduleId),
        rows = store.getRows();

    colKeys.forEach(colKey =>
        store.setColumnProps(colKey, { visible: false }, { shouldUpdate: false }),
    );

    rows.forEach(row =>
        colKeys.forEach(colKey =>
            store.setCellProps(row.key, colKey, { visible: false }),
        ),
    );

    store.forceUpdate();
}

/**
 * 显示列
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {ColKey | ColKey[]} colKeys
 */
export function showColByKey(moduleId, colKeys) {
    !Array.isArray(colKeys) && (colKeys = [colKeys]);
    let store = getStore.call(this, moduleId),
        rows = store.getRows();

    colKeys.forEach(key => {
        store.setColumnProps(key, { visible: true }, { shouldUpdate: false });
    });

    rows.forEach(row =>
        colKeys.forEach(key =>
            store.setCellProps(row.key, key, { visible: false }),
        ),
    );

    store.forceUpdate();
}

/**
 * 获取选中行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {boolean} getCrossPageFlag 是否获取跨页选中行
 * @returns {Array<{ data: TableRow | { pk: string, ts: string }, index: DataIndex, pageIndex: number }>} 当前页的数据是行数据，非当前页的数据是pk和ts
 */
export function getCheckedRows(moduleId, getCrossPageFlag = true) {
    let store = getStore.call(this, moduleId),
        checkedKeys = store.getCheckedKeys(),
        allCheckedKeys = store?.getAllCheckedKeys(),
        allBodyCheckedKeys = store.getAllBodyCheckedKeys(),
        allInfo = store.getAllRowsInfo(),
        pageInfo = store.getPaginationInfo(),
        rows = store.getRows(),
        flatInfo = store.getFlatInfo(),
        checkedRows = [],
        { crossPageSelect, pkname, childPkName } = store.getConfig();

    /** @detail 跨页选时获取的全部选中行数据 */
    if (getCrossPageFlag && pkname && crossPageSelect) {
        let currentPageRowKeys = new Map(rows.map(row => [row.values[pkname].value, row.key])),
            pkIndexes = {},
            checkedPkOrChildPk = [...allCheckedKeys],
            allpks = [...allInfo.allpks];

        // YBP-FI-BUG-8060
        allpks.splice(pageInfo?.pageIndex * pageInfo?.pageSize, rows.length, ...rows.map(row => row.values[pkname].value));
        allpks.forEach((pk, index) => pkIndexes[pk] = index);

        if (flatInfo.tableType == "flat") {
            currentPageRowKeys = new Map(rows.map(row => [row.values[childPkName].value, row.key])); //替换成子表pk和rowId的对应关系
            allInfo.allbodypks.forEach((bodyPk, index) => pkIndexes[bodyPk] = index); //替换成子表pk和index的对应关系
            checkedPkOrChildPk = [...allBodyCheckedKeys];
        }
        //allKeys如果是主表则代表主表pk，如果是子表模式，则代表子表pk
        checkedPkOrChildPk.sort((pkA, pkB) => pkIndexes[pkA] - pkIndexes[pkB]).forEach(pk => {
            if (currentPageRowKeys.has(pk)) {
                let row = store.getRowData(currentPageRowKeys.get(pk));
                if (!row) { return; }
                checkedRows.push({
                    data: row,
                    index: row.dataIndex,
                    pageIndex: pageInfo?.pageIndex,
                });
            } else {
                let pkIndex = pkIndexes[pk];
                if (pkIndex === -1) { return; }
                checkedRows.push({
                    data: { pk, ts: allInfo.alltss?.[pkIndex] || '' },
                    index: pkIndex % pageInfo.pageSize,
                    pageIndex: Math.floor(pkIndex / pageInfo.pageSize),
                });
            }
        });

    } else {
        /** @detail 非跨页选时获取的当前页选中行数据 */
        checkedKeys = new Set([...checkedKeys].sort((rowKeyA, rowKeyB) =>
            store.getRowData(rowKeyA) && store.getRowData(rowKeyB) ? store.getRowData(rowKeyA).viewIndex - store.getRowData(rowKeyB).viewIndex : 0,
        ));
        rows.forEach(row => {
            if (checkedKeys.has(row.key)) {
                checkedRows.push({
                    data: row,
                    index: row.dataIndex,
                });
            }
        });
    }
    if (flatInfo.tableType == "flat") { //统一处理拉平模式下，默认返回整单的。
        let parentCheckedRows = [];
        let pkValue = new Set(); //存储已经设置过的pk
        let { pkRelationMap = {} } = store.getFlatRelation() || {};
        let { childToParent = {} } = pkRelationMap;
        checkedRows.forEach(row => {
            let { data } = row;
            if (data.pk) {
                let parentPk = childToParent[data.pk];
                row.data = { ...data, pk: parentPk };
                if (!pkValue.has(parentPk)) {
                    parentCheckedRows.push(row);
                    pkValue.add(parentPk);
                }
            }
            else {
                let currentPkValue = data.values[pkname].value;
                if (!pkValue.has(currentPkValue)) {
                    parentCheckedRows.push(row);
                    pkValue.add(currentPkValue);
                }
            }
        });
        checkedRows = parentCheckedRows;
    }
    return checkedRows || [];
}

/**
 * 选中/取消选中所有行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {boolean} checked
 */
export function selectAllRows(moduleId, checked) {
    execWithShare.call(this, moduleId, shareId => {
        let store = getStore.call(this, shareId);
        if (checked) {
            store?.checkAll();
        } else {
            store?.setCheckedKeys(new Set());
            store?.setAllCheckedKeys(new Set());
            store?.setAllBodyCheckedKeys(new Set());
        }
        // 已知和其他表格的不同：不需要触发selectedChange
    });
}

/**
 * 反选
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 */
export function reverseSelected(moduleId) {
    execWithShare.call(this, moduleId, shareId => {
        let store = getStore.call(this, shareId);

        store?.checkReverse();
        // 已知和其他表格的不同：不需要触发selectedChange
    });
}

/**
 * 通过行index删除行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {DataIndex|DataIndex[]} dataIndexes
 */
export function deleteTableRowsByIndex(moduleId, dataIndexes) {
    let store = getStore.call(this, moduleId),
        rows = store.getRows(),
        checkedKeys = store.getCheckedKeys(),
        flatInfo = store.getFlatInfo();
    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);
    dataIndexes = dataIndexes.map(index => Number(index));
    if (flatInfo.tableType == "flat") {
        dataIndexes = transformDataIndex(store, dataIndexes);
    }
    let deleteRows = rows.filter(row => dataIndexes.includes(row.dataIndex));

    deleteRows.forEach(row => checkedKeys.delete(row.key));

    execWithShare.call(this, moduleId, shareId => {
        let shareStore = getStore.call(this, shareId);
        shareStore?.setCheckedKeys(checkedKeys);
        shareStore?.deleteRow(deleteRows.map(row => row.key), true);
        // 已知和其他表格的不同：不需要触发selectedChange
    });
}

/**
 * 通过行RowId删除行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {RowKey|RowKey[]} rowKeys
 */
export function deleteTableRowsByRowId(moduleId, rowKeys) {
    let store = getStore.call(this, moduleId),
        checkedKeys = store.getCheckedKeys(),
        flatInfo = store.getFlatInfo(),
        { rowIdRelationMap } = store.getFlatRelation(),
        flatRowKeys = new Set();
    !Array.isArray(rowKeys) && (rowKeys = [rowKeys]);
    if (flatInfo.tableType == "flat") {
        rowKeys.forEach(rowKey => {
            rowIdRelationMap[rowKey].forEach(relationRowKey => flatRowKeys.add(relationRowKey));
        });
        rowKeys = Array.from(flatRowKeys);
    }
    rowKeys.forEach(rowKey => checkedKeys.delete(rowKey));

    execWithShare.call(this, moduleId, shareId => {
        let shareStore = getStore.call(this, shareId);
        shareStore?.setCheckedKeys(checkedKeys);
        shareStore?.deleteRow(rowKeys, true);
        // 已知和其他表格的不同：不需要触发selectedChange
    });
}

/**
 * 通过index新增行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {TableRow} data
 * @param {DataIndex} dataIndex
 */
export function addRow(moduleId, data, dataIndex) {
    let store = getStore.call(this, moduleId);

    typeof dataIndex === 'undefined' && (dataIndex = store.getVisibleRows().length);

    data.rows = data.rows.map(row => ({ ...row, key: row.rowid || row.rowId || row.key || generateKey() }));

    execWithShare.call(this, moduleId, shareId => {
        getStore.call(this, shareId)?.addRow(dataIndex, data.rows, { changeStatus: false });
    });
}

/**
 * 设置弹窗里指定项的值
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey
 * @param {string | boolean | { refname:string, refpk:string }} value 只有type为'refer'时为{refname, refpk}对象
 * @param {string} type
 */
export function setTableValueBykey(moduleId, colKey, value, type) {
    let store = getStore.call(this, moduleId);

    if (type == 'refer') {
        store.setSimpleTableModalValue(colKey, {
            display: value.refname,
            value: value.refpk,
        });
    } else {
        store.setSimpleTableModalValue(colKey, {
            value: value,
            display: null,
        });
    }
}

/**
 * 设置列的编辑属性
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey
 * @param {boolean} disabled
 */
export function setTableValueDisabled(moduleId, colKey, disabled) {
    let store = getStore.call(this, moduleId),
        rows = store.getRows();

    store.setColumnProps(colKey, { disabled });

    rows.forEach(row => store.setCellProps(row.key, colKey, { disabled }));
}

/**
 * 设置列必输属性
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey
 * @param {boolean} required
 */
export function setTableValueRequired(moduleId, colKey, required) {
    let store = getStore.call(this, moduleId),
        rows = store.getRows();

    store.setColumnProps(colKey, { required });

    rows.forEach(row => store.setCellProps(row.key, colKey, { required }));
}

/**
 * 通过index聚焦行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {DataIndex} dataIndex
 */
export function focusRowByIndex(moduleId, dataIndex) {
    let store = getStore.call(this, moduleId),
        rowKey = store.getRowKeys()[dataIndex];

    /** @detail 滚动到行 */
    store.scrollIntoView({ rowKey });

    /** @detail 背景色加深 */
    store.setClickedRow(rowKey);
}

/**
 * 通过index更新多行的数据
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {Array<{ data: TableRow, index: DataIndex }>} rows
 * @param {boolean} isCancelChecked - 是否取消选中状态
 * @param {boolean} delChildData - 子表数据为{},则不更新子表
 */
export function updateDataByIndexs(moduleId, rows, isCancelChecked = true, delChildData = true) {
    let store = getStore.call(this, moduleId);
    let { tableType } = store.getFlatInfo();
    let rowKeys = store.getRowKeys(),
        checkedKeys = new Set([...store.getCheckedKeys()]); // 避免直接修改checkedKeys引用
    if (tableType == "flat") {
        let indexes = rows.map(row => row.index);
        let indexRelation = createIndexRelation(store, indexes);
        let relationRows = [];
        let addedIndex = new Set();
        let leafColumns = store.getVisibleLeafColumns();
        rows.forEach(({ data: row, index: dataIndex }) => {
            if (delChildData) {
                Object.keys(row.values).forEach(colKey => {
                    let targetCol = leafColumns.find(col => col.attrcode === colKey) || {};
                    if (targetCol.isChildCol && !Object.keys(row.values[colKey]).length) {
                        delete row.values[colKey];
                    }
                });
            }
            indexRelation[dataIndex].forEach(relationIndex => {
                if (!addedIndex.has(relationIndex)) {
                    relationRows.push({ data: row, index: relationIndex });
                }
                addedIndex.add(relationIndex);
            });
        });
        rows = relationRows;
    }
    rows = rows.map(({ data: row, index: dataIndex }) => {
        isCancelChecked && checkedKeys.delete(rowKeys[dataIndex]);
        return { ...store.getRowData(rowKeys[dataIndex]), ...row };
    });

    execWithShare.call(this, moduleId, shareId => {
        let shareStore = getStore.call(this, shareId);
        /** @detail 合并数据 */
        shareStore?.setRowData(rows);

        isCancelChecked && shareStore?.setCheckedKeys(checkedKeys);
    });
}

/**
 * 通过pk更新多行的数据
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {Array<{ data: TableRow, index: DataIndex }>} rows
 * @param customCompare  //自定义比较函数
 */
export function updateDataByPk(moduleId, rows, isCancelChecked = true, customCompare) {
    let store = getStore.call(this, moduleId),
        { pkname, childPkName } = store.getConfig(),
        { tableType } = store.getFlatInfo(),
        allRows = store.getRows(),
        checkedKeys = new Set([...store.getCheckedKeys()]), // 避免直接修改checkedKeys引用
        pkName = tableType == "flat" ? childPkName : pkname;
    let updateRows = rows.map(row => {
        let targetRow = allRows.find(viewRow => {
            if (typeof customCompare === "function") {
                return customCompare(row, viewRow);
            } else {
                if (viewRow.values[pkName].value === row.values[pkName].value) {
                    return true;
                }
            }
        });
        if (targetRow) {
            isCancelChecked && checkedKeys.delete(targetRow.key || targetRow.rowId || targetRow.rowid);
            return { ...targetRow, ...row };
        } else {
            return row;
        }
    });
    execWithShare.call(this, moduleId, shareId => {
        let shareStore = getStore.call(this, shareId);
        /** @detail 合并数据 */
        shareStore?.setRowData(updateRows);
        isCancelChecked && shareStore?.setCheckedKeys(checkedKeys);
    });
}

/**
 * 通过pk更新ts
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {Array} pks  要更新的主表pks
 * @param {Array} ts   要更新的主表ts   pks和ts是对应的。 [pk1,pk2,pk3] [ts1,ts2,ts3]
 */
export function updateTsByPk(moduleId, pks, ts) {
    let store = getStore.call(this, moduleId),
        allRowsInfo = store?.getAllRowsInfo(),
        { allbodypks, pkrelation, allpks } = allRowsInfo,
        { tableType } = store.getFlatInfo(),
        isFlat = tableType === "flat";
    let tsIndexInfo = []; // [{index:2,ts:xxx}]
    if (isFlat) {
        let bodyPkToIndex = {};
        allbodypks.forEach((bodyPk, index) => bodyPkToIndex[bodyPk] = index);
        pks.forEach((pk, pkIndex) => pkrelation[pk].forEach(bodyPk => tsIndexInfo.push({ index: bodyPkToIndex[bodyPk], ts: ts[pkIndex] })));

    } else {
        let pksToIndex = {};
        allpks.forEach((pk, index) => pksToIndex[pk] = index);
        pks.forEach((pk, pkIndex) => tsIndexInfo.push({ index: pksToIndex[pk], ts: ts[pkIndex] }));
    }

    tsIndexInfo.forEach(tsIndex => {
        let { ts, index } = tsIndex;
        allRowsInfo.alltss[index] = ts;
    });
}

/**
 * 根据rowKey设置表格某行某个字段值
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @param {CellValue} cellValue
 */
export function setValByKeyAndRowId(moduleId, rowKey, colKey, cellValue) {
    execWithShare.call(this, moduleId, shareId => getStore.call(this, shareId)?.setCellValue(rowKey, colKey, { value: cellValue }));
}

/**
 * 设置选中状态
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {DataIndex[] | DataIndex} index
 * @param {boolean} checked 为true时按模式操作，否则删除指定选中数据
 * @param {boolean} isCallBack - 2207未实现
 * @param {{ pattern: 'set' | 'add' | 'delete' | 'clear' }} param 仅checked为true时生效，set（默认）：替换，add：追加，delete：删除，clear：清空
 */
export function selectTableRows(moduleId, index, checked, isCallBack, param = {}) {
    execWithShare.call(this, moduleId, shareId => {
        let store = getStore.call(this, shareId);
        if (!store) { return; }

        let { pattern = 'set' } = param,
            rowKeys = store.getRowKeys(),
            indexes = Array.isArray(index) ? index : [index],
            checkedKeys;

        if (checked) {
            switch (pattern) {
                case 'set':
                    store.setCheckedKeys(new Set(indexes.map(index => rowKeys[index])));
                    break;
                case 'add':
                    checkedKeys = store.getCheckedKeys();
                    indexes.forEach(index => checkedKeys.add(rowKeys[index]));
                    store.setCheckedKeys(checkedKeys);
                    break;
                case 'delete':
                    checkedKeys = store.getCheckedKeys();
                    indexes.forEach(index => checkedKeys.delete(rowKeys[index]));
                    store.setCheckedKeys(checkedKeys);
                    break;
                case 'clear':
                    store.setCheckedKeys(new Set());
                    break;
                default:
                    break;
            }
        } else {
            checkedKeys = store.getCheckedKeys();
            indexes.forEach(index => checkedKeys.delete(rowKeys[index]));
            store.setCheckedKeys(checkedKeys);
        }
    });
}

/**
 * 获取指定页码和页容量的pks
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {number} pageIndex 不传取当前数据
 * @param {number} pageSize 不传取当前数据
 * @returns {string[]}
 */
export function getPks(moduleId, pageIndex, pageSize) {
    let store = getStore.call(this, moduleId),
        paginationInfo = store.getPaginationInfo(),
        { allpks } = store.getAllRowsInfo();

    if (!pageIndex) {
        pageIndex = Number(paginationInfo.pageIndex);
    }

    if (!pageSize) {
        pageSize = Number(paginationInfo.pageSize);
    }

    return allpks?.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize) || [];
}

/**
 * 是否有缓存数据
 * @memberof SimpleTable
 * @param {string} dataSource 缓存的key值
 * @returns {boolean}
 */
export function hasCacheData(dataSource) {
    let _runContext = this.getQiankunContext();
    if (dataSource) {
        let mcData = ViewModel.getData(dataSource, _runContext);
        if (mcData && mcData.simpleTable && mcData.simpleTable.rows && mcData.simpleTable.rows.length) {
            return true;
        }
    }

    return !!(dataSource && ViewModel.getData(dataSource, _runContext)?.simpleTable?.rows?.length);

}

/**
 * 删除pks
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {number[] | number} pks 要删除的pk
 */
export function deleteCacheId(moduleId, pks) {
    let store = getStore.call(this, moduleId),
        allRowsInfo = store.getAllRowsInfo(),
        tssIndex = new Set(),
        { pkRelationMap = {} } = store.getFlatRelation(),
        { parentToChildren } = pkRelationMap,
        isFlat = store.getFlatInfo().tableType === "flat",
        allCheckedKeys = store.getAllCheckedKeys(),
        allBodyCheckedKeys = store.getAllBodyCheckedKeys();

    Array.isArray(pks) ? (pks = new Set(pks)) : (pks = new Set([pks]));

    allRowsInfo.allpks.forEach((pk, index) => {
        if (pks.has(pk)) {
            tssIndex.add(index);
        }
    });

    // 删除allpks中的pk
    allRowsInfo.allpks = allRowsInfo.allpks.filter(pk => !pks.has(pk));
    // 删除allpks中的tss
    allRowsInfo.alltss = allRowsInfo.alltss.filter((ts, index) => !tssIndex.has(index));
    // 删除allPks中的allbodypks
    if (isFlat) {
        let deleteAllBodyPks = [];
        pks.forEach(pk => {
            parentToChildren[pk].forEach(childPk => {
                deleteAllBodyPks.push(childPk);
            });
        });
        allRowsInfo.allbodypks = allRowsInfo.allbodypks.filter(bodyPk => !new Set(deleteAllBodyPks).has(bodyPk));
        deleteAllBodyPks.forEach(bodyPk => allBodyCheckedKeys.delete(bodyPk));
    }

    // 删除allCheckedKeys中的pk
    pks.forEach(pk => allCheckedKeys.delete(pk));
    execWithShare.call(this, moduleId, shareId => {
        let shareStore = getStore.call(this, shareId);
        shareStore?.setAllRowsInfo(allRowsInfo);
        shareStore?.setAllCheckedKeys(allCheckedKeys);
        isFlat && shareStore?.setAllBodyCheckedKeys(allBodyCheckedKeys);
    });
}

/**
 * 增加pks
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {string[] | string} pk
 */
export function addCacheId(moduleId, pk) {
    let store = getStore.call(this, moduleId),
        allRowsInfo = store.getAllRowsInfo();

    allRowsInfo.allpks = [...new Set(allRowsInfo.allpks.concat(pk))];

    execWithShare.call(this, moduleId, shareId => {
        let shareStore = getStore.call(this, shareId);
        shareStore?.setAllRowsInfo(allRowsInfo);
    });
}

/**
 * 通过rowKey设置表格行数据
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {{ rows: TableRow[] }} data
 */
export function updateTableData(moduleId, data) {
    execWithShare.call(this, moduleId, shareId => {
        let shareStore = getStore.call(this, shareId);
        /** @detail 替换数据 */
        shareStore?.setRowData(data.rows);
    });
}

/**
 * 设置当前点击行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {{ index: ViewIndex }} data
 */
export function setClickRowIndex(moduleId, data) {
    let store = getStore.call(this, moduleId);

    store.setClickedRow(store.getVisibleRowKeys()[data.index]);
}

/**
 * 获取当前点击行
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @returns {{ record: TableRow, index: DataIndex }}
 */
export function getClickRowIndex(id) {
    let store = getStore.call(this, id),
        rowKey = store.getClickedRow(),
        row = store.getRowData(rowKey);

    if (!rowKey) { return; }

    return { record: row, index: row.dataIndex };
}

/**
 * 根据index设置单元格值
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {DataIndex} dataIndex
 * @param {ColKey} colKey
 * @param {CellValue} value
 */
export function setValByKeyAndIndex(moduleId, dataIndex, colKey, value) {
    let store = getStore.call(this, moduleId);

    let rowKeys = store.getRowKeys(),
        rowKey = rowKeys[dataIndex];

    execWithShare.call(this, moduleId, shareId => getStore.call(this, shareId)?.setCellValue(rowKey, colKey, { value }));
}

/**
 * 更新附件值
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @param {number} num  附件数量
 * @param {string} pkname  主键名
 * @param {string} pkValue  主键值
 */
export function updateAttachmentValue(moduleId, num, pkname, pkValue) {
    let store = getStore.call(this, moduleId),
        rows = store.getRows(),
        cols = store.getVisibleColumns(),
        { rowId } = rows.find(row => row.values[pkname].value === pkValue) || {};
    let value = `${num},${pkValue}`;
    cols.forEach(col => {
        if (col.itemtype === "attachment") {
            execWithShare.call(this, moduleId, shareId => getStore.call(this, shareId)?.setCellValue(rowId, col.attrcode, { value: { value } }));
        }
    });
}

/**
 * 差异更新,更新已有数据，忽略不存在的数据
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {Array<{ index: DataIndex, values: TableRow }>} rows
 */
export function updateDiffDataByIndex(moduleId, rows) {
    let store = getStore.call(this, moduleId),
        rowKeys = store.getRowKeys(),
        updatedRows = [];

    rows.forEach(({ index, values }) => {
        let rowKey = rowKeys[index],
            currentRow = store.getRowData(rowKey);

        if (!currentRow) { return; }

        /** @detail 合并数据 */
        Object.keys(values).forEach(key => {
            currentRow.values[key] = { ...currentRow.values[key], ...values[key] };
        });
        updatedRows.push(currentRow);
    });

    execWithShare.call(this, moduleId, shareId => getStore.call(this, shareId)?.setRowData(updatedRows));
}

/**
 * 更新表格高度 - 2207已废弃
 * @memberof SimpleTable
 */
export function updateTableHeight() { }

/**
 * 更新表格高度 - 2207已废弃
 * @memberof SimpleTable
 */
export function beforeAreaChangeHeight() { }

/**
 * 获取排序配置
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @returns {{ mode: 'single', sortParam: Array<{ field: ColKey, order: 'ascend' | 'default' | 'descend' }> }}
 */
export function getSortParam(moduleId) {
    let store = getStore.call(this, moduleId),
        sortInfo = store.getSortInfo();

    return sortInfo.size ? {
        mode: 'single', // 目前只有single
        sortParam: [...sortInfo].map(([colKey, order]) => {
            if (order === 1) {
                order = 'ascend';
            } else if (order === 0) {
                order = 'default';
            } else {
                order = 'descend';
            }
            return {
                field: colKey,
                order,
            };
        }),
    } : undefined;
}

/**
 * 更新指定数据
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey 指定条件：该列数据不为空
 * @param {TableRow[]} nextRows
 * @param {string} sagaErrorMessage
 */
export function updateDataByRefresh(moduleId, colKey, nextRows, sagaErrorMessage) {
    let store = getStore.call(this, moduleId);

    if (Array.isArray(nextRows)) {
        let rows = store.getRows();

        let groupedRowsByKey = rows.reduce((group, row) => {
            group[row.values[colKey].value] = row;
            return group;
        }, {});

        nextRows = nextRows.filter(row => ![null, undefined, ''].includes(row[colKey])).map(row => {
            let rawData = groupedRowsByKey[row[colKey]];
            return {
                ...rawData,
                values: {
                    ...rawData.values,
                    ...Object.entries(row).reduce((values, [key, value]) => {
                        values[key] = { value };
                        return values;
                    }, {}),
                    saga_errormesg: row.saga_status === '1' ? sagaErrorMessage : '',
                },
            };
        });

        /** @detail 只更新已有数据 */
        execWithShare.call(this, moduleId, shareId => getStore.call(this, shareId)?.setRowData(nextRows));
    }
}

/**
 * 2207未实现
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {object} param
 */
export function saveFilterCustomColData(moduleId, { attrcode, rowId, value }) { }

/**
 * 获取当前获取焦点的行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {DataIndex} index
 */
export function setCacheFocusIndex(moduleId, index) {
    let store = getStore.call(this, moduleId),
        rowKeys = store.getRowKeys();

    store.setLastFocusedRow(rowKeys[index]);
}

/**
 * 设置列属性
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {Object<ColKey, ColumnProps>} newColumn
 */
export function setColumn(moduleId, newColumn) {
    let store = getStore.call(this, moduleId),
        rows = store.getRows(),
        colKeys = Object.keys(newColumn);

    colKeys.forEach(colKey => store.setColumnProps(colKey, newColumn[colKey]));
    rows.forEach(row => colKeys.forEach(colKey => store.setCellProps(row.key, colKey, newColumn[colKey])));
}

/**
 * 获取列属性
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey
 * @returns {ColumnProps}
 */
export function getColumn(moduleId, colKey) {
    let store = getStore.call(this, moduleId);

    return store.getColumnProps(colKey);
}

/**
 * 在指定位置插入行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {Array<{ index: DataIndex, data: TableRow }>} record
 * @returns {{ rows: TableRow[] }}
 */
export function insertDataByIndexs(moduleId, record) {
    let store = getStore.call(this, moduleId);

    record.forEach(({ index: dataIndex, data: row }) =>
        execWithShare.call(this, moduleId, shareId =>
            getStore.call(this, shareId)?.addRow(dataIndex, row, { changeStatus: false }),
        ),
    );
    return { rows: store.getRows() };
}

/**
 * 在所有行的最后追加数据
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {ColKey} pkname
 * @param {TableRow[]} nextRows
 */
export function addTableData(moduleId, pkname, nextRows = []) {
    let store = getStore.call(this, moduleId);

    let pageInfo = store.getPaginationInfo(),
        rows = store.getRows(),
        allRowsInfo = store.getAllRowsInfo(),
        allPksSet = new Set(allRowsInfo.allpks ?? []);

    // 过滤掉已有的行
    nextRows = nextRows.filter(row => !allPksSet.has(row.values[pkname]?.value));

    /** @detail 把新的pkname加入allpks里 */
    allRowsInfo.allpks = allRowsInfo.allpks.concat(nextRows.map(row => row.values[pkname]?.value));

    /** @detail 更新分页数据的总条数 */
    pageInfo.total = allRowsInfo.allpks.length;

    execWithShare.call(this, moduleId, shareId => {
        let shareStore = getStore.call(this, shareId);
        shareStore?.setAllRowsInfo(allRowsInfo);
        shareStore?.setPaginationInfo(pageInfo);
    });

    /** @detail 更新当前页数据，不超过页显示容量 */
    if (pageInfo.pageSize > rows.length) {
        // 追加到原始数据后面，不能超过当前pageSize
        nextRows = nextRows.slice(0, pageInfo.pageSize - rows.length);
        if (nextRows.length) {
            execWithShare.call(this, moduleId, shareId => {
                getStore.call(this, shareId)?.addRow(rows.length, nextRows, { changeStatus: false });
            });
        }
    }
}

/**
 * 设置禁用选中行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {Object<RowKey, boolean>} disabledValues
 */
export function setDisabledRows(moduleId, disabledValues) {
    let store = getStore.call(this, moduleId),
        disabledKeys = store.getDisabledKeys();

    Object.entries(disabledValues).forEach(([rowKey, disabled]) => {
        if (disabled) {
            disabledKeys.add(rowKey);
        } else {
            disabledKeys.delete(rowKey);
        }
    });

    execWithShare.call(this, moduleId, shareId => getStore.call(this, shareId)?.setDisabledKeys(disabledKeys));
}

/**
 * 获取禁用选中行的key值
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @returns {string[]}
 */
export function getDisabledRows(moduleId) {
    let store = getStore.call(this, moduleId);

    return [...store.getDisabledKeys()];
}

/**
 * 更新合计行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 */
export function updateTotal(moduleId) {
    let store = getStore.call(this, moduleId);
    store.updateTotal();
}

/**
 * 获取活动单元格（批选）
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @returns {{activeCells:Object<RowKey, ColKey[]>, activeSpecialCells: Array<{ attrcode: ColKey, label: string }>}} activeCells：表体的，activeSpecialCells：表头的
 */
export function getActiveCells(id) {
    let store = getStore.call(this, id),
        activeCells = store.getSelectedCell();

    return {
        activeCells: [...activeCells].reduce((activeCells, [rowKey, colKeys]) => {
            // rowKey为负数是表头的，这里不处理
            if (typeof rowKey === 'string') {
                activeCells[rowKey] = [...colKeys];
            }
            return activeCells;
        }, {}),
        activeSpecialCells: [...activeCells].reduce((activeCells, [rowKey, colKeys]) => {
            // rowKey为负数是表头的
            if (typeof rowKey === 'number') {
                activeCells = activeCells.concat([...colKeys].map(colKey => {
                    return { attrcode: colKey, label: store.getColumnProps(colKey)?.label };
                }));
            }
            return activeCells;
        }, []),
    };
}

/**
 * 设置单元格属性
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @param {Object} props
 */
export function setCellProps(id, rowKey, colKey, props) {
    let store = getStore.call(this, id);
    store.setCellProps(rowKey, colKey, props);
}

/**
 * 用于变换create时传入的config
 */
export function _setConfigTranslator(id, translator) {
    let store = getStore.call(this, id);
    store.setConfigTranslator(translator);
}

/**
 * 获取拉平信息
 * @memberof SimpleTable
 * @param {ModuleId} id
 */
export function getFlatInfo(id) {
    let store = getStore.call(this, id);
    return store?.getFlatInfo();
}

/**
 * 创建拉平按钮
 * @memberof SimpleTable
 * @param {ModuleId} id
 */

export function createFlatButton(moduleId, config = {}) {
    let store = getStore.call(this, moduleId);
    return <FlatButton
        store={store}
        moduleId={moduleId}
        changeTableType={config.changeTableType}
    />;
}

//  ---------------  内部方法调用  ------------------
export function getStore(moduleId) { //主子表模式下领域可能传子表id
    let tableId = moduleId;
    if (this.tableDataSource?.flatRelation?.[moduleId]) {
        tableId = this.tableDataSource.flatRelation[moduleId].parentId;
    }

    return this.tableDataSource[tableId];
}

export function getParentId(id) { //获取主表ID
    let tableId = id;
    if (this.tableDataSource?.flatRelation?.[id]) {
        tableId = this.tableDataSource.flatRelation[id].parentId;
    }
    return tableId;
}

function setStore(moduleId) {
    this.tableDataSource[moduleId] = SimpleTable.useSimpleTable();
    return this.tableDataSource[moduleId];
}

function setFlatIdRelation(param) {
    let { moduleId, childTableCode } = param;
    childTableCode.forEach(code => {
        if (!this.tableDataSource?.flatRelation) {
            this.tableDataSource.flatRelation = {};
        }
        this.tableDataSource.flatRelation[code] = { parentId: moduleId };
    });
}

function execWithShare(id, callback) {
    let store = getStore.call(this, id),
        shareIds = new Set([id]);

    if (store.getGridTableId()) { // 入参id是表格id，看看是不是卡片表格的
        shareIds.add(store.getGridTableId());
    } else if (store.getConfig()?.tableId) { // 入参是卡片表格的id
        shareIds.add(store.getConfig().tableId);
    }

    [...shareIds].forEach(tabIds => {
        callback(tabIds);
    });
    this.controlRule.willRun('setTableData');
}

function createIndexRelation(store, indexes) {
    let { rowIdRelationMap } = store.getFlatRelation();
    let rowDataMap = store.getRowDataMap();
    let rows = store.getRows();
    let indexRelationMap = {};
    indexes.forEach(index => {
        let rowId = rows[index].rowId || rows[index].rowid || rows[index].rowKey;
        let relationRowId = rowIdRelationMap[rowId];
        let indexRelation = relationRowId.map(rowId => rowDataMap[rowId].dataIndex);
        indexRelationMap[index] = indexRelation;
    });
    return indexRelationMap;
}

function transformDataIndex(store, indexes) {
    let newIndex = [];
    let indexRelation = createIndexRelation(store, indexes);
    indexes.forEach(index => {
        newIndex = [...newIndex, ...indexRelation[index]];
    });
    return newIndex;
}
// pks一般是主表pk
function transformParentPksToChild(store, pks) {
    let { pkRelationMap } = store.getFlatRelation();
    let { parentToChildren } = pkRelationMap;
    let newPks = new Set();
    pks.forEach(pk => {
        parentToChildren[pk].forEach(relationPk => {
            newPks.add(relationPk);
        });
    });
    return newPks;
}